// Templates de stories 9:16: 10 modelos prontos, ate 10 templates proprios
// (recorte 9:16), padrao geral ou por loja e geracao com download da imagem.
// A imagem e desenhada pelo bot com o mesmo motor da marca d'agua.

import { useState } from "react";
import { Trash2 } from "lucide-react";
import { CUSTOM_STORY_DESIGN, MAX_CUSTOM_STORY_TEMPLATES, STORY_PRESETS, isFeatureAllowed } from "@lucralink/shared";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { PageHeader } from "@/components/PageHeader";
import { StoryThumb } from "@/components/stories/StoryThumb";
import { StoryTemplateUpload } from "@/components/stories/StoryTemplateUpload";
import { StoryGenerator } from "@/components/stories/StoryGenerator";
import { StoryDefaults } from "@/components/stories/StoryDefaults";
import { useEntitlements } from "@/lib/entitlements";
import { useTenant } from "@/lib/hooks/useTenant";
import { useDeleteStoryTemplate, useStorySettings, useStoryTemplates, type CustomStoryTemplate } from "@/lib/hooks/useStories";

function Section({ title, description, children }: { title: string; description: string; children: JSX.Element }): JSX.Element {
  return (
    <Card variant="raised">
      <CardHeader className="border-b border-border bg-[hsl(var(--surface-raised)/0.45)]">
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="pt-5">{children}</CardContent>
    </Card>
  );
}

function CustomList({ templates, onMessage }: { templates: readonly CustomStoryTemplate[]; onMessage: (m: string, ok: boolean) => void }): JSX.Element {
  const remove = useDeleteStoryTemplate();
  if (templates.length === 0) return <p className="text-sm text-muted-foreground">Nenhum template próprio ainda.</p>;
  return (
    <ul className="flex flex-wrap gap-4">
      {templates.map((t) => (
        <li key={t.id} className="space-y-1.5">
          <StoryThumb design={CUSTOM_STORY_DESIGN} backgroundUrl={t.previewUrl} />
          <div className="flex w-[120px] items-center justify-between gap-1">
            <span className="truncate text-xs text-foreground">{t.name}</span>
            <Button
              size="icon-sm"
              variant="ghost"
              aria-label={`Excluir ${t.name}`}
              disabled={remove.isPending}
              onClick={() =>
                remove.mutateAsync(t.id).then(
                  (warning) => onMessage(warning ?? "Template excluído.", warning === null),
                  (e: unknown) => onMessage(e instanceof Error ? e.message : "Erro ao excluir.", false),
                )
              }
            >
              <Trash2 size={12} />
            </Button>
          </div>
        </li>
      ))}
    </ul>
  );
}

export function StoriesPage(): JSX.Element {
  const { data: tenantBilling } = useTenant();
  const entitlements = useEntitlements(tenantBilling?.tenant);
  const { data: templates = [], isError } = useStoryTemplates();
  const { data: settings } = useStorySettings();
  const [message, setMessage] = useState<{ text: string; ok: boolean } | null>(null);
  const notify = (text: string, ok: boolean): void => setMessage({ text, ok });
  const isPro = isFeatureAllowed(entitlements, "customTemplates");

  return (
    <div className="max-w-6xl space-y-6">
      <PageHeader title="Templates de stories" subtitle="Artes 9:16 com foto, título, preço e desconto da oferta, prontas para baixar e postar." />
      {message && <p role={message.ok ? "status" : "alert"} className={message.ok ? "text-sm text-primary" : "text-sm text-destructive"}>{message.text}</p>}
      <Section title="Gerar story" description="Escolha uma oferta e um template. Os dados vêm do link (título, preço, desconto, cupom).">
        <StoryGenerator customTemplates={templates} onError={(m) => notify(m, false)} />
      </Section>
      <Section title="Modelos prontos" description="10 modelos do LucraLink, liberados em todos os planos.">
        <ul className="flex flex-wrap gap-4">
          {STORY_PRESETS.map((p) => (
            <li key={p.key} className="space-y-1">
              <StoryThumb design={p.design} />
              <p className="w-[120px] truncate text-center text-xs text-foreground">{p.name}</p>
            </li>
          ))}
        </ul>
      </Section>
      <Section title={`Meus templates (${templates.length}/${MAX_CUSTOM_STORY_TEMPLATES})`} description="Envie sua arte de fundo: o centro recebe a foto, o nome e o preço do produto. Plano Pro.">
        <div className="space-y-5">
          {isError && <p role="alert" className="text-sm text-destructive">Não foi possível carregar seus templates.</p>}
          <CustomList templates={templates} onMessage={notify} />
          {templates.length < MAX_CUSTOM_STORY_TEMPLATES && <StoryTemplateUpload tenantId={tenantBilling?.tenant?.id ?? null} disabled={!isPro} onDone={notify} />}
          {!isPro && <p className="text-xs text-muted-foreground">Templates próprios fazem parte do plano Pro.</p>}
        </div>
      </Section>
      <Section title="Template padrão" description="Usado quando você não escolhe um template, no geral ou por loja.">
        <StoryDefaults settings={settings ?? { defaultRef: null, byProvider: {} }} customTemplates={templates} onMessage={notify} />
      </Section>
    </div>
  );
}
